// Звук мета-интерфейса. До ревизии 2026-08-31 мета была немой ЦЕЛИКОМ: во всех
// четырнадцати экранах звучал ровно один осциллятор — блип ползунка громкости
// (uiSfx.playVolumeBlip), и тот существовал не ради отклика, а чтобы было слышно,
// какую громкость выставляешь.
//
// Почему не переиспользован LevelAudio плеера: тот знает про слушателя, стерео-
// панораму, BGM-луп и громкость музыки — мете из этого не нужно ничего. Ей нужен
// короткий отклик на ЖЕСТ: всегда по центру, всегда на полной громкости sfx.
// Общий с плеером здесь только приём — ваншот из буфера плюс осцилляторный фолбэк,
// пока буфер не приехал.
//
// ГДЕ ЛЕЖАТ ФАЙЛЫ. Рядом с иконками меты (`web/public/assets/meta/sfx/`), то есть их
// отдаёт тот же веб-сервер, что и приложение, — а НЕ S3 через каталог ассетов.
// Каталог приезжает асинхронно (`loadAssetsCatalog`), а звук нажатия обязан быть на
// ПЕРВЫЙ клик: ждать сетевого ответа ради отклика на кнопку нельзя.
//
// АВТОПЛЕЙ. Контекст рождается suspended, пока пользователь не сделал жест. Звать
// playMetaSfx можно откуда угодно — первый же вызов из обработчика жеста снимет
// блокировку; вызов не из жеста просто ничего не сыграет и не сломается.

import { getPrefs } from './prefsStore';
import type { ProgressGain } from '@/save/saveStore';

export type MetaSfxName = 'press' | 'back' | 'denied' | 'award' | 'unlock';

const BASE = '/assets/meta/sfx';

/** Осцилляторный фолбэк: играет, пока WAV качается, и на случай, если файла нет
 *  вовсе (дев-сборка без выложенных ассетов). Тембр грубый намеренно — задача
 *  только в том, чтобы интерфейс не молчал и звуки отличались друг от друга. */
interface Chirp { type: OscillatorType; from: number; to: number; dur: number; peak: number; delay?: number }

const RECIPES: Record<MetaSfxName, Chirp[]> = {
  press:  [{ type: 'triangle', from: 700, to: 460, dur: 0.07, peak: 0.32 }],
  back:   [{ type: 'triangle', from: 460, to: 620, dur: 0.07, peak: 0.26 }],
  denied: [{ type: 'square',   from: 220, to: 150, dur: 0.13, peak: 0.22 }],
  award: [
    { type: 'sine', from: 880,    to: 880,    dur: 0.12, peak: 0.28 },
    { type: 'sine', from: 1318.5, to: 1318.5, dur: 0.16, peak: 0.24, delay: 0.06 },
  ],
  unlock: [
    { type: 'triangle', from: 330, to: 330, dur: 0.1,  peak: 0.3 },
    { type: 'triangle', from: 660, to: 990, dur: 0.22, peak: 0.28, delay: 0.08 },
  ],
};

let ctx: AudioContext | null = null;
const buffers = new Map<MetaSfxName, AudioBuffer>();
const loading = new Set<MetaSfxName>();

/** Ленивый контекст меты — ОДИН на всё приложение: второй AudioContext стоил бы
 *  ещё одного аудио-потока, а браузеры их лимитируют. Его же берёт uiSfx. */
export function metaAudioContext(): AudioContext | null {
  if (ctx) return ctx;
  if (typeof AudioContext === 'undefined') return null;   // jsdom/SSR — тишина
  try { ctx = new AudioContext(); } catch { return null; }
  return ctx;
}

function loadBuffer(c: AudioContext, name: MetaSfxName): void {
  if (buffers.has(name) || loading.has(name)) return;
  loading.add(name);
  void fetch(`${BASE}/${name}.wav`)
    .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
    .then((bytes) => c.decodeAudioData(bytes))
    .then((buf) => { buffers.set(name, buf); })
    .catch(() => { /* файла нет или формат не декодировался — живём на фолбэке */ })
    .finally(() => loading.delete(name));
}

/** Сыграть звук интерфейса. Громкость — из настроек (`prefs.sfx`), ноль = тишина. */
export function playMetaSfx(name: MetaSfxName): void {
  const c = metaAudioContext();
  if (!c) return;
  const vol = getPrefs().sfx;
  if (vol <= 0) return;
  void c.resume();
  loadBuffer(c, name);

  const gain = c.createGain();
  gain.gain.value = vol;
  gain.connect(c.destination);

  const buffer = buffers.get(name);
  if (buffer) {
    const one = c.createBufferSource();
    one.buffer = buffer;
    one.connect(gain);
    one.start();
    return;
  }
  const t0 = c.currentTime;
  for (const r of RECIPES[name]) {
    const start = t0 + (r.delay ?? 0);
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = r.type;
    osc.frequency.setValueAtTime(r.from, start);
    if (r.to !== r.from) osc.frequency.exponentialRampToValueAtTime(r.to, start + r.dur);
    g.gain.setValueAtTime(r.peak, start);
    g.gain.exponentialRampToValueAtTime(0.001, start + r.dur);
    osc.connect(g);
    g.connect(gain);
    osc.start(start);
    osc.stop(start + r.dur + 0.02);   // нода умирает сама — утечек на спам-кликах нет
  }
}

/** Что сыграть на клик по элементу. Отдельная чистая функция — её и проверяет тест,
 *  не трогая ни DOM-делегат, ни WebAudio.
 *
 *  Правила: явный `data-sfx` побеждает; заблокированная кнопка отвечает отказом, а
 *  не щелчком (иначе нажатие на замок Хардкода звучит как удачное); всё остальное
 *  с классом `.btn` — обычное нажатие. Кит требует этот класс от каждой кнопки, так
 *  что делегат покрывает и те экраны, которых ещё нет. */
export function metaSfxForTarget(el: Element | null): MetaSfxName | null {
  const btn = el?.closest?.('[data-sfx], button, .btn') ?? null;
  if (!btn) return null;
  const explicit = btn.getAttribute('data-sfx');
  if (explicit) return RECIPES[explicit as MetaSfxName] ? (explicit as MetaSfxName) : null;
  if (!btn.classList.contains('btn')) return null;
  const disabled = (btn as HTMLButtonElement).disabled
    || btn.getAttribute('aria-disabled') === 'true';
  return disabled ? 'denied' : 'press';
}

/** Один слушатель на весь интерфейс вместо обработчика на каждой кнопке: их в мете
 *  больше сотни в десяти экранах, общего компонента `<Button>` нет, а класс `.btn`
 *  есть у всех (UI-кит). Вешается в Shell, снимается вместе с ним.
 *
 *  Слушаем pointerdown, а не click: отклик должен совпасть с НАЖАТИЕМ, как и сдвиг
 *  кнопки на +3px в ките, — на click он опоздает на длину жеста. Отсюда же ручной
 *  разбор клавиатуры: с клавиатуры pointerdown не приходит вовсе. */
export function installMetaSfx(root: Document | HTMLElement = document): () => void {
  const onPointer = (e: Event) => {
    const name = metaSfxForTarget(e.target as Element);
    if (name) playMetaSfx(name);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const name = metaSfxForTarget(e.target as Element);
    if (name) playMetaSfx(name);
  };
  root.addEventListener('pointerdown', onPointer);
  root.addEventListener('keydown', onKey as EventListener);
  return () => {
    root.removeEventListener('pointerdown', onPointer);
    root.removeEventListener('keydown', onKey as EventListener);
  };
}


// Наградные звуки играют не мгновенно, а с задержкой, и это не украшательство: в момент
// победы плеер запускает шестисекундную фанфару `win`, и чимка, выпущенная в ту же
// миллисекунду, попадает ровно в её атаку — там её не слышно. Смещаем на фразу вперёд.
// Это ЕДИНСТВЕННЫЕ два числа для тюнинга; каналы разные (фанфара идёт на громкости
// музыки, эти — на громкости эффектов), так что задержка решает маскировку, а не микс.
const AWARD_DELAY_MS = 900;
const UNLOCK_DELAY_MS = 1800;

/** Озвучить то новое, что принёс результат уровня: звёзды и открывшийся следующий.
 *  Возвращает отмену — экран обязан звать её при размонтировании, иначе разблокировка
 *  догонит игрока уже на карте. */
export function playProgressSfx(gain: ProgressGain): () => void {
  const timers: ReturnType<typeof setTimeout>[] = [];
  if (gain.starsGained > 0) {
    // один раз на окно, а не на каждую звезду: три чимки подряд за полсекунды
    // слышатся не наградой, а очередью
    timers.push(setTimeout(() => playMetaSfx('award'), AWARD_DELAY_MS));
  }
  if (gain.firstCompletion) {
    timers.push(setTimeout(() => playMetaSfx('unlock'), UNLOCK_DELAY_MS));
  }
  return () => { for (const t of timers) clearTimeout(t); };
}
